import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback, useEffect } from "react";
import { useProgrammaticGraphemePinyinAudio } from "#use-programmatic-grapheme-pinyin-audio";

type PinyinAudioCard = {
  pinyinList: readonly { toneNumbersPinyin: string }[];
} & ({ grapheme: string } | { front: string });

export type LearnMnemonicsProgrammaticAudio = {
  playForPinyin: (pinyin: string) => void;
  /** Plays every study pinyin in order, or only those `shouldPlay` admits. */
  playSequence: (shouldPlay?: (toneNumbersPinyin: string) => boolean) => void;
};

/**
 * Programmatic pinyin audio for the learn layout. Every play is initiated by
 * an explicit press handler (lesson card reveal, "Next Pronunciation" button).
 * Audio is never autoplayed by a mount or navigation effect.
 *
 * When the host screen loses focus any in-flight playback is cancelled so the
 * previous card's pinyin sequence doesn't bleed into the next card. We don't
 * call `stop()` on unmount: `useAudioPlayer` releases its native shared object
 * on unmount and pausing after that release throws.
 */
export function useLearnMnemonicsProgrammaticAudio(
  card: PinyinAudioCard | null,
): LearnMnemonicsProgrammaticAudio {
  const [isMutedOverride] = useContextSelectors(NavContext, (c) => c.isMutedOverride);
  const fallbackCard: PinyinAudioCard = card ?? { grapheme: "", pinyinList: [] };
  const { playForPinyin, playSequence, stop } = useProgrammaticGraphemePinyinAudio(fallbackCard, {
    isMuted: isMutedOverride,
  });
  const isFocused = useIsFocused();

  useEffect(() => {
    if (!isFocused) {
      stop();
    }
  }, [isFocused, stop]);

  const playForPinyinCallback = useCallback(
    (pinyin: string) => {
      if (card === null) {
        return;
      }
      void playForPinyin(pinyin);
    },
    [card, playForPinyin],
  );

  const playSequenceCallback = useCallback(
    (shouldPlay?: (toneNumbersPinyin: string) => boolean) => {
      if (card === null) {
        return;
      }
      void playSequence(shouldPlay);
    },
    [card, playSequence],
  );

  return {
    playForPinyin: playForPinyinCallback,
    playSequence: playSequenceCallback,
  };
}
